import { rnd } from '@edicom/common/string/rnd';

/**
 * Devuelve la clase FbPopupCellRenderer, para que el grid la instancie cuando pinta la celda en modo edición.
 * Esta clase centraliza metodos para usar el popup del grid, renderiza en especial la directiva <fb-grid-popup>
 *
 * @param {FbCellRenderer} FbCellRenderer se pasa la clase como injección de dependencia
 * @param {Object} options opciones que se pasan de clase en clase (FbPopupCellRenderer -> FbCellRenderer -> EcCellRenderer)
 * @param {Object} methods metodos que se usan para personalizar la celda.
 * @param {function} methods.renderPopup debe devolver el contenido html del popup, se llama con ({value, data})
 * @returns {FbPopupCellRenderer}
 */

const defaultOptions = { ecCellName: 'FbPopupCellRenderer' };

export function FbPopupCellRendererClass(FbCellRenderer, options, { renderPopup } = {}) {
  return class FbPopupCellRenderer extends FbCellRenderer {
    constructor(currentOptions = {}) {
      super(Object.assign({}, defaultOptions, options, currentOptions));

      if (typeof renderPopup !== 'function') {
        throw new Error('FbPopupCellRenderer: "popup.templateFn" obligatorio');
      }
    }

    render(params) {
      return super.render.call(this, params);
    }

    postrender({ value, data, original }) {
      this.isVisiblePopup = () => this?.eCellPopup?.isVisible?.();
      this.closePopup = () => this?.eCellPopup?.close?.();
      this.openPopup = () => {
        if (!this.eCellPopup) {
          // si el popup no existe, lo creamos y lo abrimos:
          return createPopup.call(this).then(() => {
            return this.eCellPopup.open();
          });
        }

        return Promise.resolve().then(() => {
          if (!this.isVisiblePopup()) {
            return this.eCellPopup.open();
          }
        });

        function createPopup() {
          return new Promise(resolve => {
            // init del scope angularjs
            this.fbInitScope();

            // inyectamos el html en la celda:
            const { fullscreen = false, xPath } = FbCellRenderer.getMetadata(original);
            const id = `FbPopupCellRenderer_id_${original.model}_${rnd()}`;
            const attrFullscreen = fullscreen ? `fullscreen` : ``;
            const attrLabel = original.title ? `label="${original.title}"` : ``;

            const eDiv = document.createElement('div');
            eDiv.innerHTML = `
              <template id="${id}_template">${renderPopup({ value, data })}</template>
              <fb-grid-popup for="${id}" ${attrFullscreen} opened ${attrLabel} x-path="${xPath}"></fb-grid-popup>
          `;

            this.eCellRenderer.id = id;
            this.eCellRenderer.appendChild(eDiv);
            this.eCellPopup = eDiv.querySelector(`[for=${id}]`);

            // compilamos. Se va abrir automaticamente ya que el fb-grid-popup lleva el atributo "opened"
            this.$compile(angular.element(eDiv))(this.scope);
            this.$timeout(() => {
              resolve();
            });
          });
        }
      };
    }

    onClick(event) {
      // si la celda es editable,
      // o se ha hecho click pero no en el icono
      // -> no hacemos nada al click. Dejamos la mano al EcCellRenderer para entrar en modo edición o no...
      if (this.isEditable || !event.target.closest('[on-click-cell="icon"]')) {
        return;
      }

      // si la celda NO es editable, y se ha hecho click en el icono
      this.isVisiblePopup() ? this.closePopup() : this.openPopup();
    }
  };
}
